import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { toast } from '../ui/use-toast';
import { useAuth } from '../../contexts/PhpAuthContext';
import { useData } from '../../contexts/DataContext';
import { apiRequest, API_BASE_URL } from '../../config/api';
import { UserPlus, Edit, Save, X, Mail, Phone, Calendar, Upload, User, Filter, Eye, EyeOff, Search, ChevronLeft, ChevronRight, Check, Clock, FileText, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import PatientFormsWizard from './PatientFormsWizard';
import ReviewSubmitPage from './ReviewSubmitPage';

const AdminPatientsTab = ({ onSubmissionComplete, viewAsAdmin = false }) => {
  const { profile } = useAuth();
  const { patients, fetchPatients, addPatient, updatePatient, fetchSubmissions } = useData();
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  // For doctors, default to 'my' (All my patients), for admin default to 'all'
  const [addedByFilter, setAddedByFilter] = useState(() => {
    // This will be set properly in useEffect based on profile
    return 'all';
  }); // 'all', 'my', 'others', or creator ID
  const [creators, setCreators] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  
  const [newPatient, setNewPatient] = useState({
    patient_unique_id: '',
    name: '',
    phone: '',
    sex: '',
    email: '',
    username: '',
    password: '',
    date_of_birth: '',
    status: 'Active',
    profile_photo: null,
    description: '',
    address: '',
    branch: '',
    is_child: false,
    parent_phone: '',
    parent_name: '',
    parent_patient_id: ''
  });
  
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);
  const [patientIdError, setPatientIdError] = useState('');
  const [isCheckingId, setIsCheckingId] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const [dobError, setDobError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  const [viewMode, setViewMode] = useState('detailed'); // 'detailed' or 'list'
  const [isSearchingParent, setIsSearchingParent] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedPatientForAppointment, setSelectedPatientForAppointment] = useState(null);
  const [appointmentData, setAppointmentData] = useState({
    patient_name: '',
    phone: '',
    date: '',
    service_type: 'Checkup',
    time_slot: '',
    status: 'Pending'
  });
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [showMedicalHistory, setShowMedicalHistory] = useState(false);
  const [selectedPatientForHistory, setSelectedPatientForHistory] = useState(null);
  const [showReviewPage, setShowReviewPage] = useState(false);
  const [reviewPatientData, setReviewPatientData] = useState(null);

  // Set default filter for doctors to 'my' (All my patients)
  // If viewAsAdmin is true for chief_doctor, default to 'all' like admin
  useEffect(() => {
    if (!profile) return;
    
    const isAdminView = profile.role === 'admin' || (profile.role === 'chief_doctor' && viewAsAdmin);
    
    if (isAdminView) {
      // Admin view: ensure filter is set to 'all' to show all patients
      if (addedByFilter === 'my' || addedByFilter === 'others') {
        setAddedByFilter('all');
      }
    } else {
      // Doctor view: set to 'my' if currently 'all'
      if (profile.role === 'doctor' || profile.role === 'chief_doctor' || profile.role === 'clinic_login') {
        if (addedByFilter === 'all') {
          setAddedByFilter('my');
        }
      }
    }
  }, [profile, viewAsAdmin]);

  // Fetch list of creators (admins/doctors) for filter
  useEffect(() => {
    const fetchCreators = async () => {
      try {
        const data = await apiRequest('get_creators.php');
        if (data.success) {
          setCreators(data.creators);
        }
      } catch (error) {
        console.error('Error fetching creators:', error);
      }
    };
    fetchCreators();
  }, []);

  // Auto-generate Patient Unique ID when Add Patient form opens
  useEffect(() => {
    if (showAddPatient && !newPatient.patient_unique_id) {
      const generatePatientId = async () => {
        try {
          const data = await apiRequest('get_next_patient_id.php');
          if (data.success) {
            setNewPatient(prev => ({ ...prev, patient_unique_id: data.patient_unique_id }));
          }
        } catch (error) {
          console.error('Error generating patient ID:', error);
          // Fallback: generate client-side
          setNewPatient(prev => ({ ...prev, patient_unique_id: 'GDC-101' }));
        }
      };
      generatePatientId();
    }
  }, [showAddPatient]);

  // Check if Patient Unique ID already exists
  const checkPatientId = async (patientId, excludeId = null) => {
    if (!patientId || patientId.trim() === '') {
      setPatientIdError('');
      return true;
    }

    setIsCheckingId(true);
    setPatientIdError('');

    try {
      let url = `check_patient_id.php?patient_id=${encodeURIComponent(patientId)}`;
      if (excludeId) {
        url += `&exclude_id=${excludeId}`;
      }
      const data = await apiRequest(url);
      
      if (data.success) {
        if (data.exists) {
          setPatientIdError('This Patient ID already exists in the database. Please use a different ID.');
          setIsCheckingId(false);
          return false;
        } else {
          setPatientIdError('');
          setIsCheckingId(false);
          return true;
        }
      } else {
        setPatientIdError('Error checking Patient ID availability');
        setIsCheckingId(false);
        return false;
      }
    } catch (error) {
      setPatientIdError('Error checking Patient ID availability');
      setIsCheckingId(false);
      return false;
    }
  };

  // Handle Patient Unique ID change with validation
  const handlePatientIdChange = async (value) => {
    setNewPatient({ ...newPatient, patient_unique_id: value });
    setPatientIdError('');
    
    if (value && value.trim() !== '') {
      // Validate format (GDC-XXX)
      if (!/^GDC-\d+$/.test(value)) {
        setPatientIdError('Invalid format. Use format: GDC-XXX (e.g., GDC-101)');
        return;
      }
      
      // Check for duplicates
      await checkPatientId(value);
    }
  };

  // Handle Edit Patient Unique ID change with validation
  const handleEditPatientIdChange = async (value) => {
    setEditingPatient({ ...editingPatient, patient_unique_id: value });
    setPatientIdError('');
    
    if (value && value.trim() !== '') {
      // Validate format (GDC-XXX)
      if (!/^GDC-\d+$/.test(value)) {
        setPatientIdError('Invalid format. Use format: GDC-XXX (e.g., GDC-101)');
        return;
      }
      
      // Check for duplicates (excluding current patient)
      await checkPatientId(value, editingPatient?.id);
    }
  };

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  };

  // Check if patient is a child (below 21)
  const isChild = (dateOfBirth) => {
    const age = calculateAge(dateOfBirth);
    return age !== null && age < 21;
  };

  // Validate DOB
  const validateDOB = (dob) => {
    if (!dob) {
      setDobError('');
      return true;
    }

    const selectedDate = new Date(dob);
    const today = new Date();
    const threeMonthsAgo = new Date(today);
    threeMonthsAgo.setMonth(today.getMonth() - 3);
    const maxDate = new Date(today);
    maxDate.setFullYear(today.getFullYear() - 99);

    // Check if date is in the future
    if (selectedDate > today) {
      setDobError('Date of birth cannot be in the future');
      return false;
    }

    // Check if date is within 3 months from today
    if (selectedDate > threeMonthsAgo) {
      setDobError('Date of birth must be at least 3 months ago');
      return false;
    }

    // Check if date is more than 99 years ago
    if (selectedDate < maxDate) {
      setDobError('Date of birth cannot be more than 99 years ago');
      return false;
    }

    setDobError('');
    return true;
  };

  // Check if phone number is unique (for adults only)
  const checkPhoneUniqueness = async (phone, dateOfBirth, excludeId = null) => {
    if (!phone || phone.trim() === '') {
      setPhoneError('');
      return true;
    }

    // If it's a child, phone can be shared - no check needed
    if (dateOfBirth && isChild(dateOfBirth)) {
      setPhoneError('');
      return true;
    }

    setIsCheckingPhone(true);
    setPhoneError('');

    try {
      let url = `check_patient_phone.php?phone=${encodeURIComponent(phone)}`;
      if (dateOfBirth) {
        url += `&date_of_birth=${encodeURIComponent(dateOfBirth)}`;
      }
      if (excludeId) {
        url += `&exclude_id=${excludeId}`;
      }
      const data = await apiRequest(url);
      
      if (data.success) {
        if (data.exists) {
          setPhoneError('This phone number is already registered to an adult patient. Phone numbers must be unique for patients 21 years and older.');
          setIsCheckingPhone(false);
          return false;
        } else {
          setPhoneError('');
          setIsCheckingPhone(false);
          return true;
        }
      } else {
        setPhoneError('Error checking phone number availability');
        setIsCheckingPhone(false);
        return false;
      }
    } catch (error) {
      setPhoneError('Error checking phone number availability');
      setIsCheckingPhone(false);
      return false;
    }
  };

  // Auto-fill username and password from phone number
  const handlePhoneChange = async (phone) => {
    const cleanedPhone = phone.replace(/\D/g, ''); // Remove non-digits
    setNewPatient({
      ...newPatient,
      phone: phone,
      username: cleanedPhone, // Auto-fill username with phone (digits only)
      password: cleanedPhone  // Auto-fill password with phone (digits only)
    });
    
    // Check phone uniqueness for adults
    if (newPatient.date_of_birth) {
      await checkPhoneUniqueness(phone, newPatient.date_of_birth);
    }
  };

  // Handle DOB change with validation
  const handleDOBChange = async (dob) => {
    setNewPatient({ ...newPatient, date_of_birth: dob });
    
    // Validate DOB
    validateDOB(dob);
    
    // Check if it's a child
    const child = isChild(dob);
    if (!child && newPatient.is_child) {
      // If was child but now adult, uncheck child checkbox and clear parent fields
      setNewPatient({ 
        ...newPatient, 
        date_of_birth: dob, 
        is_child: false, 
        parent_phone: '',
        parent_name: '',
        parent_patient_id: ''
      });
    }
    
    // Check phone uniqueness if phone is already entered
    if (newPatient.phone) {
      await checkPhoneUniqueness(newPatient.phone, dob);
    }
  };

  // Search for parent by phone number
  const searchParentByPhone = async (phone) => {
    if (!phone || phone.trim() === '') {
      setNewPatient({
        ...newPatient,
        parent_phone: '',
        parent_name: '',
        parent_patient_id: ''
      });
      return;
    }

    setIsSearchingParent(true);
    try {
      const data = await apiRequest(`get_patient_by_phone.php?phone=${encodeURIComponent(phone)}`);
      
      if (data.success && data.found) {
        // Auto-populate parent name and patient ID
        setNewPatient({
          ...newPatient,
          parent_phone: phone,
          parent_name: data.patient.full_name,
          parent_patient_id: data.patient.patient_unique_id
        });
      } else {
        // Parent not found - clear patient ID but keep phone and allow manual name entry
        setNewPatient({
          ...newPatient,
          parent_phone: phone,
          parent_name: newPatient.parent_name || '', // Keep existing name if any
          parent_patient_id: '' // Clear patient ID
        });
      }
    } catch (error) {
      console.error('Error searching parent:', error);
      // On error, keep phone but clear patient ID
      setNewPatient({
        ...newPatient,
        parent_phone: phone,
        parent_patient_id: ''
      });
    } finally {
      setIsSearchingParent(false);
    }
  };

  // Handle parent phone change
  const handleParentPhoneChange = async (phone) => {
    setNewPatient({ ...newPatient, parent_phone: phone });
    // Search for parent when phone is entered
    if (phone && phone.trim() !== '') {
      await searchParentByPhone(phone);
    } else {
      // Clear parent fields if phone is cleared
      setNewPatient({
        ...newPatient,
        parent_phone: '',
        parent_name: '',
        parent_patient_id: ''
      });
    }
  };

  // Handle parent phone change for edit form
  const handleParentPhoneChangeEdit = async (phone) => {
    setEditingPatient({ ...editingPatient, parent_phone: phone });
    // Search for parent when phone is entered
    if (phone && phone.trim() !== '') {
      setIsSearchingParent(true);
      try {
        const data = await apiRequest(`get_patient_by_phone.php?phone=${encodeURIComponent(phone)}`);
        
        if (data.success && data.found) {
          // Auto-populate parent name and patient ID
          setEditingPatient({
            ...editingPatient,
            parent_phone: phone,
            parent_name: data.patient.full_name,
            parent_patient_id: data.patient.patient_unique_id
          });
        } else {
          // Parent not found - clear patient ID but keep phone and allow manual name entry
          setEditingPatient({
            ...editingPatient,
            parent_phone: phone,
            parent_name: editingPatient.parent_name || '', // Keep existing name if any
            parent_patient_id: '' // Clear patient ID
          });
        }
      } catch (error) {
        console.error('Error searching parent:', error);
        // On error, keep phone but clear patient ID
        setEditingPatient({
          ...editingPatient,
          parent_phone: phone,
          parent_patient_id: ''
        });
      } finally {
        setIsSearchingParent(false);
      }
    } else {
      // Clear parent fields if phone is cleared
      setEditingPatient({
        ...editingPatient,
        parent_phone: '',
        parent_name: '',
        parent_patient_id: ''
      });
    }
  };

  // Handle child checkbox change
  const handleChildCheckboxChange = (checked) => {
    setNewPatient({
      ...newPatient,
      is_child: checked,
      parent_phone: checked ? newPatient.parent_phone : '',
      parent_name: checked ? newPatient.parent_name : '',
      parent_patient_id: checked ? newPatient.parent_patient_id : ''
    });
    
    // Clear phone error if checking child (children can share phones)
    if (checked) {
      setPhoneError('');
    } else if (newPatient.phone && newPatient.date_of_birth) {
      // Re-check phone uniqueness if unchecking child
      checkPhoneUniqueness(newPatient.phone, newPatient.date_of_birth);
    }
  };

  const handleAddPatient = async () => {
    // Validate mandatory fields
    if (!newPatient.name || !newPatient.phone || !newPatient.sex || !newPatient.username || !newPatient.password || !newPatient.branch) {
      toast({
        title: 'Error',
        description: 'Please fill in all mandatory fields (Full Name, Phone, Sex, Username, Password, Branch)',
        variant: 'destructive'
      });
      return;
    }

    // Validate Patient Unique ID
    if (!newPatient.patient_unique_id || newPatient.patient_unique_id.trim() === '') {
      toast({
        title: 'Error',
        description: 'Patient Unique ID is required',
        variant: 'destructive'
      });
      return;
    }

    // Check Patient ID format
    if (!/^GDC-\d+$/.test(newPatient.patient_unique_id)) {
      toast({
        title: 'Error',
        description: 'Invalid Patient ID format. Use format: GDC-XXX (e.g., GDC-101)',
        variant: 'destructive'
      });
      return;
    }

    // Check if Patient ID already exists before submitting
    const isAvailable = await checkPatientId(newPatient.patient_unique_id);
    if (!isAvailable || patientIdError) {
      toast({
        title: 'Error',
        description: patientIdError || 'This Patient ID already exists in the database. Please use a different ID.',
        variant: 'destructive'
      });
      return;
    }

    // Validate child and parent information
    if (newPatient.is_child) {
      if (!newPatient.parent_phone) {
        toast({
          title: 'Error',
          description: 'Parent phone number is required for children below 21 years',
          variant: 'destructive'
        });
        return;
      }
      if (!newPatient.parent_name) {
        toast({
          title: 'Error',
          description: 'Parent name is required for children below 21 years',
          variant: 'destructive'
        });
        return;
      }
    }

    try {
      const result = await addPatient(newPatient);
      
      if (result.error) {
        toast({
          title: 'Error',
          description: result.error.message || 'Failed to add patient',
          variant: 'destructive'
        });
        return;
      }
      
      // Reset form
      setNewPatient({
        patient_unique_id: '',
        name: '',
        phone: '',
        sex: '',
        email: '',
        username: '',
        password: '',
        date_of_birth: '',
        status: 'Active',
        profile_photo: null,
        description: '',
        address: '',
        branch: '',
        is_child: false,
        parent_phone: '',
        parent_name: '',
        parent_patient_id: ''
      });
      setProfilePhotoPreview(null);
      setPatientIdError('');
      setShowAddPatient(false);
      
      // Refresh patients list
      const isAdminView = profile?.role === 'admin' || (profile?.role === 'chief_doctor' && viewAsAdmin);
      if (addedByFilter && addedByFilter !== 'all') {
        fetchPatients(parseInt(addedByFilter), viewAsAdmin && profile?.role === 'chief_doctor' ? true : false);
      } else {
        fetchPatients(null, isAdminView && profile?.role === 'chief_doctor' ? viewAsAdmin : false);
      }
      
      toast({
        title: 'Patient added successfully',
        description: `${newPatient.name} has been added to the system`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add patient',
        variant: 'destructive'
      });
    }
  };
  
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: 'Error',
          description: 'Photo size should be less than 5MB',
          variant: 'destructive'
        });
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Error',
          description: 'Please upload an image file',
          variant: 'destructive'
        });
        return;
      }
      
      setNewPatient({ ...newPatient, profile_photo: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditPatient = (patient) => {
    const dob = patient.date_of_birth || '';
    const child = dob ? isChild(dob) : false;
    
    const patientData = {
      id: patient.id,
      patient_unique_id: patient.patient_unique_id || '',
      name: patient.full_name || '',
      username: patient.username || '',
      phone: patient.phone || '',
      sex: patient.sex || '',
      email: patient.email || '',
      date_of_birth: dob,
      status: patient.status || 'Active',
      profile_photo: patient.profile_photo || null,
      description: patient.description || '',
      address: patient.address || '',
      branch: patient.branch || '',
      password: '', // Don't populate password field for security
      can_reset_password: patient.can_reset_password || false, // Default to false
      is_child: child,
      parent_phone: patient.parent_phone || '',
      parent_name: patient.parent_name || '',
      parent_patient_id: patient.parent_patient_id || ''
    };
    
    setEditingPatient(patientData);
    setPatientIdError(''); // Reset error when opening edit form
    setPhoneError('');
    setDobError('');
    
    // Set photo preview if patient has a profile photo
    if (patient.profile_photo) {
      let photoUrl = patient.profile_photo;
      if (photoUrl && !photoUrl.startsWith('http') && !photoUrl.startsWith('data:')) {
        const API_BASE_URL = window.location.protocol + '//' + window.location.hostname;
        photoUrl = API_BASE_URL + (photoUrl.startsWith('/') ? photoUrl : '/' + photoUrl);
      }
      setEditPhotoPreview(photoUrl);
    } else {
      setEditPhotoPreview(null);
    }
  };

  const handleEditPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Error',
          description: 'Photo size should be less than 5MB',
          variant: 'destructive'
        });
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Error',
          description: 'Please upload an image file',
          variant: 'destructive'
        });
        return;
      }
      
      setEditingPatient({ ...editingPatient, profile_photo: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdatePatient = async () => {
    // Validate mandatory fields
    if (!editingPatient.name || !editingPatient.phone || !editingPatient.sex || !editingPatient.branch) {
      toast({
        title: 'Error',
        description: 'Please fill in all mandatory fields (Full Name, Phone, Sex, Branch)',
        variant: 'destructive'
      });
      return;
    }

    // Validate DOB
    if (editingPatient.date_of_birth && !validateDOB(editingPatient.date_of_birth)) {
      toast({
        title: 'Error',
        description: dobError || 'Invalid date of birth',
        variant: 'destructive'
      });
      return;
    }

    // Validate child and parent information
    if (editingPatient.is_child) {
      if (!editingPatient.parent_phone) {
        toast({
          title: 'Error',
          description: 'Parent phone number is required for children below 21 years',
          variant: 'destructive'
        });
        return;
      }
      if (!editingPatient.parent_name) {
        toast({
          title: 'Error',
          description: 'Parent name is required for children below 21 years',
          variant: 'destructive'
        });
        return;
      }
    }

    // Check phone uniqueness for adults
    if (!editingPatient.is_child && editingPatient.date_of_birth) {
      const isAvailable = await checkPhoneUniqueness(editingPatient.phone, editingPatient.date_of_birth, editingPatient.id);
      if (!isAvailable || phoneError) {
        toast({
          title: 'Error',
          description: phoneError || 'This phone number is already registered to an adult patient.',
          variant: 'destructive'
        });
        return;
      }
    }

    // Validate Patient Unique ID if changed
    if (editingPatient.patient_unique_id && editingPatient.patient_unique_id.trim() !== '') {
      // Check Patient ID format
      if (!/^GDC-\d+$/.test(editingPatient.patient_unique_id)) {
        toast({
          title: 'Error',
          description: 'Invalid Patient ID format. Use format: GDC-XXX (e.g., GDC-101)',
          variant: 'destructive'
        });
        return;
      }

      // Check if Patient ID already exists (excluding current patient)
      const isAvailable = await checkPatientId(editingPatient.patient_unique_id, editingPatient.id);
      if (!isAvailable || patientIdError) {
        toast({
          title: 'Error',
          description: patientIdError || 'This Patient ID already exists in the database. Please use a different ID.',
          variant: 'destructive'
        });
        return;
      }
    }

    try {
      const result = await updatePatient(editingPatient.id, editingPatient);
      
      if (result.error) {
        toast({
          title: 'Error',
          description: result.error.message || 'Failed to update patient',
          variant: 'destructive'
        });
        return;
      }
      
      setEditingPatient(null);
      setEditPhotoPreview(null);
      
      // Refresh patients list
      const isAdminView = profile?.role === 'admin' || (profile?.role === 'chief_doctor' && viewAsAdmin);
      if (addedByFilter && addedByFilter !== 'all') {
        fetchPatients(parseInt(addedByFilter), viewAsAdmin && profile?.role === 'chief_doctor' ? true : false);
      } else {
        fetchPatients(null, isAdminView && profile?.role === 'chief_doctor' ? viewAsAdmin : false);
      }
      
      toast({
        title: 'Patient updated successfully',
        description: `${editingPatient.name}'s information has been updated`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update patient',
        variant: 'destructive'
      });
    }
  };

  // Fetch patients on mount and when filter changes
  useEffect(() => {
    if (!profile) return;
    
    const isAdminView = profile?.role === 'admin' || (profile?.role === 'chief_doctor' && viewAsAdmin);
    
    // If "others" is selected but permission is not granted (and not viewing as admin), reset to "my"
    if (addedByFilter === 'others' && !profile?.can_view_other_patients && !isAdminView) {
      setAddedByFilter('my');
      return;
    }
    
    // When viewing as admin, always fetch all patients regardless of filter
    if (isAdminView) {
      if (addedByFilter && addedByFilter !== 'all' && !isNaN(parseInt(addedByFilter))) {
        // Filter by specific creator if selected
        fetchPatients(parseInt(addedByFilter), viewAsAdmin);
      } else {
        // Fetch all patients (no filter) - pass viewAsAdmin flag for chief_doctor
        fetchPatients(null, viewAsAdmin);
      }
    } else {
      // Doctor/Clinic Login view: apply normal filtering
      // Clinic login always fetches only their own patients
      if (profile?.role === 'clinic_login') {
        fetchPatients(profile.id);
      } else if (addedByFilter === 'my') {
        // Fetch patients added by the logged-in doctor
        fetchPatients(profile.id);
      } else if (addedByFilter === 'others' && profile?.can_view_other_patients) {
        // Fetch all patients, we'll filter client-side to exclude doctor's patients
        fetchPatients(null);
      } else if (addedByFilter && addedByFilter !== 'all') {
        // Fetch patients by specific creator ID
        fetchPatients(parseInt(addedByFilter));
      } else {
        // Default: fetch all patients (will be filtered client-side if needed)
        fetchPatients(null);
      }
    }
    // Reset to first page when filter changes
    setCurrentPage(1);
  }, [addedByFilter, fetchPatients, profile, viewAsAdmin]);

  // Filter patients based on search query and "Added by others" filter
  const filteredPatients = patients.filter(patient => {
    // If viewAsAdmin is true for chief_doctor, treat like admin (no filtering except by specific creator)
    const isAdminView = profile?.role === 'admin' || (profile?.role === 'chief_doctor' && viewAsAdmin);
    
    // When in admin view, only filter by specific creator if selected, otherwise show all
    if (isAdminView) {
      // Admin view: filter by specific creator if selected, otherwise show all patients
      if (addedByFilter && addedByFilter !== 'all' && !isNaN(parseInt(addedByFilter))) {
        const patientCreatorId = patient.created_by ? parseInt(patient.created_by) : null;
        const filterCreatorId = parseInt(addedByFilter);
        if (patientCreatorId !== filterCreatorId) {
          return false;
        }
      }
      // If filter is 'all' or not set, show all patients (no filtering)
    } else {
      // Doctor/Clinic Login view: Apply filtering based on addedByFilter
      if (profile?.role === 'doctor' || profile?.role === 'chief_doctor' || profile?.role === 'clinic_login') {
        // Clinic login always sees only their own patients (like doctors without can_view_other_patients)
        if (profile?.role === 'clinic_login') {
          const patientCreatorId = patient.created_by ? parseInt(patient.created_by) : null;
          const currentUserId = profile?.id ? parseInt(profile.id) : null;
          if (patientCreatorId !== currentUserId) {
            return false;
          }
        } else if (addedByFilter === 'others' && profile?.can_view_other_patients) {
          // Show only patients NOT added by the logged-in doctor
          // Only if permission is granted
          // Convert both to numbers for comparison
          const patientCreatorId = patient.created_by ? parseInt(patient.created_by) : null;
          const currentUserId = profile?.id ? parseInt(profile.id) : null;
          if (patientCreatorId === currentUserId) {
            return false;
          }
        } else if (addedByFilter === 'others' && !profile?.can_view_other_patients) {
          // If "others" is selected but permission not granted, show nothing
          return false;
        } else if (addedByFilter === 'my') {
          // Show only patients added by the logged-in doctor
          const patientCreatorId = patient.created_by ? parseInt(patient.created_by) : null;
          const currentUserId = profile?.id ? parseInt(profile.id) : null;
          if (patientCreatorId !== currentUserId) {
            return false;
          }
        } else if (addedByFilter && addedByFilter !== 'all' && !isNaN(parseInt(addedByFilter))) {
          // Show only patients added by specific creator
          const patientCreatorId = patient.created_by ? parseInt(patient.created_by) : null;
          const filterCreatorId = parseInt(addedByFilter);
          if (patientCreatorId !== filterCreatorId) {
            return false;
          }
        }
      }
    }
    
    // Apply search query filter - search by patient name, patient unique id, phone number, branch, and doctor name
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    const nameMatch = patient.full_name?.toLowerCase().includes(query);
    const idMatch = patient.patient_unique_id?.toLowerCase().includes(query);
    const phoneMatch = patient.phone?.toLowerCase().includes(query);
    const branchMatch = patient.branch?.toLowerCase().includes(query);
    const doctorNameMatch = patient.added_by_name?.toLowerCase().includes(query);
    return nameMatch || idMatch || phoneMatch || branchMatch || doctorNameMatch;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredPatients.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedPatients = filteredPatients.slice(startIndex, endIndex);

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of patient list
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  // Generate time slots based on day of week
  // Monday-Saturday: 9:30 AM to 9:00 PM (1-hour intervals)
  // Sunday: 10:00 AM to 7:00 PM (1-hour intervals)
  const generateTimeSlots = () => {
    if (!appointmentData.date) return [];
    
    const date = new Date(appointmentData.date + 'T00:00:00'); // Add time to avoid timezone issues
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const slots = [];
    
    // Generate 1-hour intervals based on day
    if (dayOfWeek === 0) {
      // Sunday: 10:00 AM to 7:00 PM (hourly on the hour)
      for (let hour = 10; hour <= 19; hour++) {
        const time = `${hour.toString().padStart(2, '0')}:00:00`;
        const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        slots.push({ value: time, label: displayTime });
      }
    } else {
      // Monday-Saturday: 9:30 AM to 9:00 PM
      // First slot: 9:30 AM
      slots.push({
        value: '09:30:00',
        label: new Date('2000-01-01T09:30:00').toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })
      });
      
      // Then hourly slots at :30 minutes: 10:30, 11:30, ..., 8:30
      for (let hour = 10; hour <= 20; hour++) {
        const time = `${hour.toString().padStart(2, '0')}:30:00`;
        const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        slots.push({ value: time, label: displayTime });
      }
      
      // Final slot: 9:00 PM
      slots.push({
        value: '21:00:00',
        label: new Date('2000-01-01T21:00:00').toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })
      });
    }
    
    return slots;
  };

  // Handle opening appointment modal
  const handleBookAppointment = (patient) => {
    setSelectedPatientForAppointment(patient);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const defaultDate = tomorrow.toISOString().split('T')[0];
    
    setAppointmentData({
      patient_name: patient.full_name || patient.name || '',
      phone: patient.phone || '',
      date: defaultDate,
      service_type: 'Checkup',
      time_slot: '',
      status: 'Pending'
    });
    setShowAppointmentModal(true);
    // Load booked slots for the default date
    loadBookedSlots(defaultDate);
  };

  // Load booked time slots for a specific date
  const loadBookedSlots = async (date) => {
    if (!date || !profile) return;
    
    try {
      const data = await apiRequest(`get_appointments.php?date=${date}&doctor_id=${profile.id}`);
      if (data.success) {
        // Normalize time format (handle both HH:MM:SS and HH:MM)
        const booked = data.appointments.map(apt => {
          const time = apt.appointment_time;
          // If time is in HH:MM format, add :00
          if (time && time.length === 5) {
            return time + ':00';
          }
          return time;
        });
        setBookedSlots(booked);
      }
    } catch (error) {
      console.error('Error loading booked slots:', error);
      setBookedSlots([]);
    }
  };

  // Handle date change
  const handleDateChange = (date) => {
    setAppointmentData({ ...appointmentData, date, time_slot: '' });
    loadBookedSlots(date);
  };

  // Handle appointment creation
  const handleCreateAppointment = async () => {
    if (!selectedPatientForAppointment || !profile) return;

    // Validate required fields
    if (!appointmentData.date || !appointmentData.time_slot) {
      toast({
        title: 'Error',
        description: 'Please select date and time slot',
        variant: 'destructive'
      });
      return;
    }

    try {
      const data = await apiRequest('add_appointment.php', {
        method: 'POST',
        body: JSON.stringify({
          patient_id: selectedPatientForAppointment.id,
          doctor_id: profile.id,
          appointment_date: appointmentData.date,
          appointment_time: appointmentData.time_slot,
          service_type: appointmentData.service_type,
          status: appointmentData.status
        })
      });

      if (data.success) {
        toast({
          title: 'Success',
          description: 'Appointment booked successfully'
        });
        setShowAppointmentModal(false);
        setSelectedPatientForAppointment(null);
        setAppointmentData({
          patient_name: '',
          phone: '',
          date: '',
          service_type: 'Checkup',
          time_slot: '',
          status: 'Pending'
        });
        // Reload booked slots
        if (appointmentData.date) {
          loadBookedSlots(appointmentData.date);
        }
      } else {
        toast({
          title: 'Error',
          description: data.error || data.message || 'Failed to book appointment',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to book appointment',
        variant: 'destructive'
      });
    }
  };

  // Get available slots count
  const getAvailableSlotsCount = () => {
    const allSlots = generateTimeSlots();
    const available = allSlots.filter(slot => !bookedSlots.includes(slot.value));
    return available.length;
  };

  return (
    <div className="space-y-6">
      {/* Header with Add Button and Filter */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <h2 className="text-xl sm:text-2xl font-semibold text-white">Manage Patients</h2>
        <div className="flex items-center space-x-3">
          {/* Filter by Added By - Show for Admin, Doctor, and Clinic Login */}
          {(profile?.role === 'admin' || profile?.role === 'doctor' || profile?.role === 'chief_doctor' || profile?.role === 'clinic_login') && (
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <Select 
                value={addedByFilter} 
                onValueChange={(value) => {
                  // Prevent selecting "others" if permission not granted (and not viewing as admin)
                  const isAdminView = profile?.role === 'admin' || (profile?.role === 'chief_doctor' && viewAsAdmin);
                  if (value === 'others' && !profile?.can_view_other_patients && !isAdminView) {
                    return; // Don't update the filter
                  }
                  setAddedByFilter(value);
                }}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white w-48">
                  <SelectValue placeholder="Filter by Added By" />
                </SelectTrigger>
                <SelectContent>
                  {(profile?.role === 'admin' || (profile?.role === 'chief_doctor' && viewAsAdmin)) ? (
                    <>
                      <SelectItem value="all">All Patients</SelectItem>
                      {creators
                        .filter((creator) => creator.role !== 'admin') // Exclude admin from dropdown
                        .map((creator) => (
                        <SelectItem key={creator.id} value={creator.id.toString()}>
                          {creator.full_name} ({creator.role === 'admin' ? 'Admin' : creator.role === 'chief_doctor' ? 'Chief Doctor' : creator.role === 'clinic_login' ? 'Clinic Login' : 'Doctor'})
                        </SelectItem>
                      ))}
                    </>
                  ) : (
                    // Doctor/Clinic Login/Chief Doctor sees: My Patients and Added by others (always visible, but disabled if permission not granted)
                    // Clinic login only sees "All My Patients" (others option is disabled)
                    <>
                      <SelectItem value="my">All My Patients</SelectItem>
                      <SelectItem 
                        value="others" 
                        disabled={profile?.role === 'clinic_login' || !profile?.can_view_other_patients}
                        className={(profile?.role === 'clinic_login' || !profile?.can_view_other_patients) ? 'opacity-50 cursor-not-allowed' : ''}
                      >
                        Added by others
                        {(profile?.role === 'clinic_login' || !profile?.can_view_other_patients) && (
                          <span className="ml-2 text-xs text-gray-400">(Disabled by Admin)</span>
                        )}
                      </SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button
            onClick={() => setShowAddPatient(true)}
            className="medical-gradient text-white hover:opacity-90"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add Patient
          </Button>
        </div>
      </div>

      {/* Informational Text - Only show if there are patients */}
      {filteredPatients.length > 0 && (
        <div className="bg-gradient-to-r from-green-600/30 via-green-500/30 to-green-600/30 border border-green-500/50 rounded-lg p-4 flex items-start gap-3 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-400/30 to-transparent animate-shimmer"></div>
          <Info className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5 relative z-10 animate-pulse" />
          <div className="flex-1 relative z-10">
            <p className="text-green-100 text-sm sm:text-base animate-fadeIn">
            Add patient → Book appointment → Add medical history.
            </p>
          </div>
        </div>
      )}

      {/* Add Patient Form */}
      {showAddPatient && (
        <Card className="glass-effect border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Add Patient</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Patient Unique ID - Auto-generated, editable */}
              <div>
                <Label htmlFor="patient_unique_id" className="text-white">Patient Unique ID *</Label>
                <Input
                  id="patient_unique_id"
                  type="text"
                  value={newPatient.patient_unique_id}
                  onChange={(e) => handlePatientIdChange(e.target.value)}
                  className={`bg-gray-800 border-gray-600 text-white ${patientIdError ? 'border-red-500' : ''}`}
                  placeholder="Auto-generated (e.g., GDC-101)"
                  required
                />
                {isCheckingId && (
                  <p className="text-xs text-blue-400 mt-1">Checking availability...</p>
                )}
                {patientIdError && (
                  <p className="text-xs text-red-400 mt-1">{patientIdError}</p>
                )}
                {!patientIdError && !isCheckingId && newPatient.patient_unique_id && (
                  <p className="text-xs text-green-400 mt-1">Patient ID is available</p>
                )}
                {!patientIdError && !isCheckingId && !newPatient.patient_unique_id && (
                  <p className="text-xs text-gray-400 mt-1">Auto-generated, can be edited</p>
                )}
              </div>

              {/* Full Name */}
              <div>
                <Label htmlFor="name" className="text-white">Full Name *</Label>
                <Input
                  id="name"
                  value={newPatient.name}
                  onChange={(e) => setNewPatient({ ...newPatient, name: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="John Smith"
                  required
                />
              </div>

              {/* Phone - Auto-fills username and password */}
              <div>
                <Label htmlFor="phone" className="text-white">Phone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={newPatient.phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="+91 9629996774"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Auto-fills username and password</p>
              </div>

              {/* Sex */}
              <div>
                <Label htmlFor="sex" className="text-white">Sex *</Label>
                <Select value={newPatient.sex} onValueChange={(value) => setNewPatient({ ...newPatient, sex: value })}>
                  <SelectTrigger id="sex" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder="Select sex" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Email - Optional */}
              <div>
                <Label htmlFor="email" className="text-white">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newPatient.email}
                  onChange={(e) => setNewPatient({ ...newPatient, email: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="patient@example.com"
                />
              </div>

              {/* Date of Birth */}
              <div>
                <Label htmlFor="date_of_birth" className="text-white">Date of Birth</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={newPatient.date_of_birth}
                  onChange={(e) => handleDOBChange(e.target.value)}
                  className={`bg-gray-800 border-gray-600 text-white ${dobError ? 'border-red-500' : ''}`}
                  min={(() => {
                    const maxDate = new Date();
                    maxDate.setFullYear(maxDate.getFullYear() - 99);
                    return maxDate.toISOString().split('T')[0];
                  })()}
                  max={(() => {
                    const minDate = new Date();
                    minDate.setMonth(minDate.getMonth() - 3);
                    return minDate.toISOString().split('T')[0];
                  })()}
                  onKeyDown={(e) => {
                    // Prevent typing more than 4 digits for year
                    if (e.target.value.length >= 10 && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && !e.key.startsWith('Arrow')) {
                      e.preventDefault();
                    }
                  }}
                />
                {dobError && (
                  <p className="text-xs text-red-400 mt-1">{dobError}</p>
                )}
                {!dobError && newPatient.date_of_birth && (
                  <p className="text-xs text-gray-400 mt-1">
                    Age: {calculateAge(newPatient.date_of_birth)} years
                    {isChild(newPatient.date_of_birth) && ' (Child - can share phone number)'}
                  </p>
                )}
              </div>

              {/* Is Child Checkbox */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_child"
                  checked={newPatient.is_child}
                  onChange={(e) => handleChildCheckboxChange(e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                  disabled={newPatient.date_of_birth ? !isChild(newPatient.date_of_birth) : false}
                />
                <Label htmlFor="is_child" className="text-white cursor-pointer">
                  Is Child (Below 21 years)
                </Label>
              </div>

              {/* Parent Phone Number - Show only if child is checked */}
              {newPatient.is_child && (
                <>
                  <div>
                    <Label htmlFor="parent_phone" className="text-white">Parent Phone Number *</Label>
                    <Input
                      id="parent_phone"
                      type="tel"
                      value={newPatient.parent_phone}
                      onChange={(e) => handleParentPhoneChange(e.target.value)}
                      className="bg-gray-800 border-gray-600 text-white"
                      placeholder="+91 9629996774"
                      required={newPatient.is_child}
                    />
                    {isSearchingParent && (
                      <p className="text-xs text-blue-400 mt-1">Searching for parent...</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">Required for children below 21 years. Will auto-populate parent name and ID if found.</p>
                  </div>

                  {/* Parent Name */}
                  <div>
                    <Label htmlFor="parent_name" className="text-white">Parent Name *</Label>
                    <Input
                      id="parent_name"
                      type="text"
                      value={newPatient.parent_name}
                      onChange={(e) => setNewPatient({ ...newPatient, parent_name: e.target.value })}
                      className="bg-gray-800 border-gray-600 text-white"
                      placeholder="Parent's full name"
                      required={newPatient.is_child}
                    />
                    <p className="text-xs text-gray-400 mt-1">Auto-filled if parent is found in database</p>
                  </div>

                  {/* Parent's Patient ID */}
                  <div>
                    <Label htmlFor="parent_patient_id" className="text-white">Parent's Patient ID</Label>
                    <Input
                      id="parent_patient_id"
                      type="text"
                      value={newPatient.parent_patient_id}
                      onChange={(e) => setNewPatient({ ...newPatient, parent_patient_id: e.target.value })}
                      className="bg-gray-800 border-gray-600 text-white"
                      placeholder="GDC-XXX (Auto-filled if parent found)"
                      readOnly
                    />
                    <p className="text-xs text-gray-400 mt-1">Auto-filled if parent is found in database. Leave blank if parent is not a patient.</p>
                  </div>
                </>
              )}

              {/* Status */}
              <div>
                <Label htmlFor="status" className="text-white">Status</Label>
                <Select value={newPatient.status} onValueChange={(value) => setNewPatient({ ...newPatient, status: value })}>
                  <SelectTrigger id="status" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Username - Auto-filled from phone, editable by admin only */}
              <div>
                <Label htmlFor="username" className="text-white">Username *</Label>
                <Input
                  id="username"
                  type="text"
                  value={newPatient.username}
                  onChange={(e) => setNewPatient({ ...newPatient, username: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Auto-filled from phone"
                  required
                  disabled={profile?.role !== 'admin'}
                />
                <p className="text-xs text-gray-400 mt-1">Used for login {profile?.role !== 'admin' && '(Auto-filled)'}</p>
              </div>

              {/* Password - Auto-filled from phone, editable by admin only */}
              <div>
                <Label htmlFor="password" className="text-white">Password *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={newPatient.password}
                    onChange={(e) => setNewPatient({ ...newPatient, password: e.target.value })}
                    className="bg-gray-800 border-gray-600 text-white pr-10"
                    placeholder="Auto-filled from phone"
                    required
                    disabled={profile?.role !== 'admin'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 h-4 w-4 text-gray-400 hover:text-gray-300 focus:outline-none"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Default password is patient phone number {profile?.role !== 'admin' && '(Auto-filled)'}</p>
              </div>

              {/* Profile Photo */}
              <div className="md:col-span-2">
                <Label htmlFor="profile_photo" className="text-white">Profile Photo</Label>
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <Input
                      id="profile_photo"
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="bg-gray-800 border-gray-600 text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600"
                    />
                  </div>
                  {profilePhotoPreview && (
                    <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-600">
                      <img src={profilePhotoPreview} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">Max size: 5MB. Accepted formats: JPG, PNG, GIF</p>
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <Label htmlFor="address" className="text-white">Address</Label>
                <Textarea
                  id="address"
                  value={newPatient.address}
                  onChange={(e) => setNewPatient({ ...newPatient, address: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Enter full address"
                  rows={3}
                />
              </div>

              {/* Branch - Mandatory */}
              <div>
                <Label htmlFor="branch" className="text-white">Select Branch *</Label>
                <Select 
                  value={newPatient.branch} 
                  onValueChange={(value) => setNewPatient({ ...newPatient, branch: value })}
                >
                  <SelectTrigger id="branch" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GDC - Main Branch 1">GDC - Main Branch 1</SelectItem>
                    <SelectItem value="GDC - Branch 2">GDC - Branch 2</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-400 mt-1">Required field</p>
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <Label htmlFor="description" className="text-white">Description</Label>
                <Textarea
                  id="description"
                  value={newPatient.description}
                  onChange={(e) => setNewPatient({ ...newPatient, description: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Optional notes about the patient"
                  rows={4}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button onClick={handleAddPatient} className="medical-gradient text-white hover:opacity-90">
                <Save className="h-4 w-4 mr-2" />
                Add Patient
              </Button>
              <Button variant="outline" onClick={() => setShowAddPatient(false)} className="border-gray-600 text-gray-300 hover:bg-gray-700">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Patient Form - Similar structure to Add form */}
      {editingPatient && (
        <Card className="glass-effect border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Edit Patient</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Similar fields as Add form, but populated with editingPatient data */}
              <div>
                <Label htmlFor="edit-patient_unique_id" className="text-white">Patient Unique ID *</Label>
                <Input
                  id="edit-patient_unique_id"
                  type="text"
                  value={editingPatient.patient_unique_id || ''}
                  onChange={(e) => handleEditPatientIdChange(e.target.value)}
                  className={`bg-gray-800 border-gray-600 text-white ${patientIdError ? 'border-red-500' : ''}`}
                  placeholder="GDC-101"
                  required
                />
                {isCheckingId && (
                  <p className="text-xs text-blue-400 mt-1">Checking availability...</p>
                )}
                {patientIdError && (
                  <p className="text-xs text-red-400 mt-1">{patientIdError}</p>
                )}
                {!patientIdError && !isCheckingId && editingPatient.patient_unique_id && (
                  <p className="text-xs text-green-400 mt-1">Patient ID is available</p>
                )}
                {!patientIdError && !isCheckingId && !editingPatient.patient_unique_id && (
                  <p className="text-xs text-gray-400 mt-1">Can be edited</p>
                )}
              </div>

              <div>
                <Label htmlFor="edit-name" className="text-white">Full Name *</Label>
                <Input
                  id="edit-name"
                  value={editingPatient.name || ''}
                  onChange={(e) => setEditingPatient({ ...editingPatient, name: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="John Smith"
                  required
                />
              </div>

              <div>
                <Label htmlFor="edit-phone" className="text-white">Phone *</Label>
                <Input
                  id="edit-phone"
                  type="tel"
                  value={editingPatient.phone || ''}
                  onChange={(e) => setEditingPatient({ ...editingPatient, phone: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="+91 9629996774"
                  required
                />
              </div>

              <div>
                <Label htmlFor="edit-sex" className="text-white">Sex *</Label>
                <Select value={editingPatient.sex || ''} onValueChange={(value) => setEditingPatient({ ...editingPatient, sex: value })}>
                  <SelectTrigger id="edit-sex" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder="Select sex" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="edit-email" className="text-white">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editingPatient.email || ''}
                  onChange={(e) => setEditingPatient({ ...editingPatient, email: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="patient@example.com"
                />
              </div>

              <div>
                <Label htmlFor="edit-date_of_birth" className="text-white">Date of Birth</Label>
                <Input
                  id="edit-date_of_birth"
                  type="date"
                  value={editingPatient.date_of_birth || ''}
                  onChange={async (e) => {
                    const dob = e.target.value;
                    validateDOB(dob);
                    const child = isChild(dob);
                    setEditingPatient({ 
                      ...editingPatient, 
                      date_of_birth: dob,
                      is_child: child,
                      parent_phone: child ? editingPatient.parent_phone : ''
                    });
                    if (editingPatient.phone) {
                      await checkPhoneUniqueness(editingPatient.phone, dob, editingPatient.id);
                    }
                  }}
                  className={`bg-gray-800 border-gray-600 text-white ${dobError ? 'border-red-500' : ''}`}
                  min={(() => {
                    const maxDate = new Date();
                    maxDate.setFullYear(maxDate.getFullYear() - 99);
                    return maxDate.toISOString().split('T')[0];
                  })()}
                  max={(() => {
                    const minDate = new Date();
                    minDate.setMonth(minDate.getMonth() - 3);
                    return minDate.toISOString().split('T')[0];
                  })()}
                  onKeyDown={(e) => {
                    // Prevent typing more than 4 digits for year
                    if (e.target.value.length >= 10 && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && !e.key.startsWith('Arrow')) {
                      e.preventDefault();
                    }
                  }}
                />
                {dobError && (
                  <p className="text-xs text-red-400 mt-1">{dobError}</p>
                )}
                {!dobError && editingPatient.date_of_birth && (
                  <p className="text-xs text-gray-400 mt-1">
                    Age: {calculateAge(editingPatient.date_of_birth)} years
                    {isChild(editingPatient.date_of_birth) && ' (Child - can share phone number)'}
                  </p>
                )}
              </div>

              {/* Is Child Checkbox - Edit Form */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit-is_child"
                  checked={editingPatient.is_child || false}
                  onChange={(e) => {
                    setEditingPatient({
                      ...editingPatient,
                      is_child: e.target.checked,
                      parent_phone: e.target.checked ? editingPatient.parent_phone : ''
                    });
                    if (e.target.checked) {
                      setPhoneError('');
                    } else if (editingPatient.phone && editingPatient.date_of_birth) {
                      checkPhoneUniqueness(editingPatient.phone, editingPatient.date_of_birth, editingPatient.id);
                    }
                  }}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                  disabled={editingPatient.date_of_birth ? !isChild(editingPatient.date_of_birth) : false}
                />
                <Label htmlFor="edit-is_child" className="text-white cursor-pointer">
                  Is Child (Below 21 years)
                </Label>
              </div>

              {/* Parent Phone Number - Edit Form - Show only if child is checked */}
              {editingPatient.is_child && (
                <>
                  <div>
                    <Label htmlFor="edit-parent_phone" className="text-white">Parent Phone Number *</Label>
                    <Input
                      id="edit-parent_phone"
                      type="tel"
                      value={editingPatient.parent_phone || ''}
                      onChange={(e) => handleParentPhoneChangeEdit(e.target.value)}
                      className="bg-gray-800 border-gray-600 text-white"
                      placeholder="+91 9629996774"
                      required={editingPatient.is_child}
                    />
                    {isSearchingParent && (
                      <p className="text-xs text-blue-400 mt-1">Searching for parent...</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">Required for children below 21 years. Will auto-populate parent name and ID if found.</p>
                  </div>

                  {/* Parent Name - Edit Form */}
                  <div>
                    <Label htmlFor="edit-parent_name" className="text-white">Parent Name *</Label>
                    <Input
                      id="edit-parent_name"
                      type="text"
                      value={editingPatient.parent_name || ''}
                      onChange={(e) => setEditingPatient({ ...editingPatient, parent_name: e.target.value })}
                      className="bg-gray-800 border-gray-600 text-white"
                      placeholder="Parent's full name"
                      required={editingPatient.is_child}
                    />
                    <p className="text-xs text-gray-400 mt-1">Auto-filled if parent is found in database</p>
                  </div>

                  {/* Parent's Patient ID - Edit Form */}
                  <div>
                    <Label htmlFor="edit-parent_patient_id" className="text-white">Parent's Patient ID</Label>
                    <Input
                      id="edit-parent_patient_id"
                      type="text"
                      value={editingPatient.parent_patient_id || ''}
                      onChange={(e) => setEditingPatient({ ...editingPatient, parent_patient_id: e.target.value })}
                      className="bg-gray-800 border-gray-600 text-white"
                      placeholder="GDC-XXX (Auto-filled if parent found)"
                      readOnly
                    />
                    <p className="text-xs text-gray-400 mt-1">Auto-filled if parent is found in database. Leave blank if parent is not a patient.</p>
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="edit-status" className="text-white">Status</Label>
                <Select value={editingPatient.status || 'Active'} onValueChange={(value) => setEditingPatient({ ...editingPatient, status: value })}>
                  <SelectTrigger id="edit-status" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="edit-username" className="text-white">Username</Label>
                <Input
                  id="edit-username"
                  type="text"
                  value={editingPatient.username || ''}
                  onChange={(e) => setEditingPatient({ ...editingPatient, username: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Enter username for login"
                  disabled={profile?.role !== 'admin'}
                />
                <p className="text-xs text-gray-400 mt-1">Used for login {profile?.role !== 'admin' && '(Admin only)'}</p>
              </div>

              <div>
                <Label htmlFor="edit-password" className="text-white">Password</Label>
                <div className="relative">
                  <Input
                    id="edit-password"
                    type={showPassword ? "text" : "password"}
                    value={editingPatient.password || ''}
                    onChange={(e) => setEditingPatient({ ...editingPatient, password: e.target.value })}
                    className="bg-gray-800 border-gray-600 text-white pr-10"
                    placeholder="Leave blank to keep current password"
                    disabled={profile?.role !== 'admin'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 h-4 w-4 text-gray-400 hover:text-gray-300 focus:outline-none"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Leave blank to keep current password {profile?.role !== 'admin' && '(Admin only)'}</p>
              </div>

              {/* Allow Password Reset Toggle - Only for Admin/Chief Doctor */}
              {(profile?.role === 'admin' || profile?.role === 'chief_doctor') && (
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg border border-gray-600">
                    <div className="flex-1">
                      <Label htmlFor="edit-can_reset_password" className="text-white font-medium">
                        Allow Password Reset
                      </Label>
                      <p className="text-xs text-gray-400 mt-1">
                        Allow this patient to reset their password in their profile settings
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        id="edit-can_reset_password"
                        checked={editingPatient.can_reset_password || false}
                        onChange={(e) => setEditingPatient({ ...editingPatient, can_reset_password: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              )}

              {/* Profile Photo */}
              <div className="md:col-span-2">
                <Label htmlFor="edit-profile_photo" className="text-white">Profile Photo</Label>
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <Input
                      id="edit-profile_photo"
                      type="file"
                      accept="image/*"
                      onChange={handleEditPhotoChange}
                      className="bg-gray-800 border-gray-600 text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600"
                    />
                  </div>
                  {editPhotoPreview && (
                    <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-600">
                      <img 
                        src={editPhotoPreview} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {editingPatient.profile_photo && !(editingPatient.profile_photo instanceof File) 
                    ? 'Current photo shown. Upload a new file to replace it.' 
                    : 'Max size: 5MB. Accepted formats: JPG, PNG, GIF'}
                </p>
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <Label htmlFor="edit-address" className="text-white">Address</Label>
                <Textarea
                  id="edit-address"
                  value={editingPatient.address || ''}
                  onChange={(e) => setEditingPatient({ ...editingPatient, address: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Enter full address"
                  rows={3}
                />
              </div>

              {/* Branch - Edit Form */}
              <div>
                <Label htmlFor="edit-branch" className="text-white">Select Branch *</Label>
                <Select 
                  value={editingPatient.branch || ''} 
                  onValueChange={(value) => setEditingPatient({ ...editingPatient, branch: value })}
                >
                  <SelectTrigger id="edit-branch" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GDC - Main Branch 1">GDC - Main Branch 1</SelectItem>
                    <SelectItem value="GDC - Branch 2">GDC - Branch 2</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-400 mt-1">Required field</p>
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <Label htmlFor="edit-description" className="text-white">Description</Label>
                <Textarea
                  id="edit-description"
                  value={editingPatient.description || ''}
                  onChange={(e) => setEditingPatient({ ...editingPatient, description: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Optional notes about the patient"
                  rows={4}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button onClick={handleUpdatePatient} className="medical-gradient text-white hover:opacity-90">
                <Save className="h-4 w-4 mr-2" />
                Update Patient
              </Button>
              <Button variant="outline" onClick={() => {
                setEditingPatient(null);
                setEditPhotoPreview(null);
              }} className="border-gray-600 text-gray-300 hover:bg-gray-700">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Bar and View Toggle */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by patient name, patient ID, phone, branch, or doctor name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-gray-800 border-gray-600 text-white"
          />
        </div>
        <div className="flex items-center gap-4">
          {searchQuery && (
            <div className="text-sm text-gray-400">
              {filteredPatients.length} result{filteredPatients.length !== 1 ? 's' : ''} found
            </div>
          )}
          {/* View Toggle */}
          <div className="flex items-center gap-2">
            <Label htmlFor="view-mode" className="text-white text-sm cursor-pointer">
              List View
            </Label>
            <input
              type="checkbox"
              id="view-mode"
              checked={viewMode === 'list'}
              onChange={(e) => setViewMode(e.target.checked ? 'list' : 'detailed')}
              className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 cursor-pointer"
            />
          </div>
        </div>
      </div>

      {/* Patients List - List View */}
      {viewMode === 'list' ? (
        <div className="space-y-3">
          {paginatedPatients.map((patient) => (
            <div
              key={patient.id}
              className="p-3 bg-gray-800 rounded-lg flex items-center justify-between gap-2 sm:gap-3 flex-wrap"
            >
              {/* Left side: Status Badge, Patient Info */}
              <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                {/* Status Badge */}
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                    patient.status === 'Active'
                      ? 'bg-green-600 text-green-100'
                      : 'bg-red-600 text-red-100'
                  }`}
                >
                  {patient.status}
                </span>
                
                {/* Patient Icon */}
                <User className="h-4 w-4 text-blue-400 flex-shrink-0" />
                
                {/* Patient Name */}
                <p className="text-white font-medium truncate text-sm sm:text-base">
                  {patient.full_name || 'Unknown Patient'}
                </p>
                
                {/* Patient ID */}
                {patient.patient_unique_id && (
                  <>
                    <span className="text-gray-500 hidden sm:inline">•</span>
                    <p className="text-gray-400 text-xs sm:text-sm truncate">
                      {patient.patient_unique_id}
                    </p>
                  </>
                )}
                
                {/* Phone */}
                {patient.phone && (
                  <>
                    <span className="text-gray-500 hidden sm:inline">•</span>
                    <div className="flex items-center text-xs sm:text-sm text-gray-400">
                      <Phone className="h-3 w-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{patient.phone}</span>
                    </div>
                  </>
                )}
                
                {/* Added By */}
                  {patient.added_by_name && (
                    <>
                      <span className="text-gray-500 hidden sm:inline">•</span>
                      <p className="text-xs sm:text-sm text-gray-400 truncate">
                        Added by: {patient.added_by_name}
                      </p>
                    </>
                  )}
                  
                  {/* Branch */}
                  {patient.branch && (
                    <>
                      <span className="text-gray-500 hidden sm:inline">•</span>
                      <p className="text-xs sm:text-sm text-gray-400 truncate">
                        Branch: {patient.branch}
                      </p>
                    </>
                  )}
              </div>
              
              {/* Right side: Action Buttons */}
              <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEditPatient(patient)}
                  className="bg-purple-600/20 border-purple-500 text-purple-300 hover:bg-purple-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                  title="Edit"
                >
                  <Edit className="h-3 w-3 sm:mr-1" />
                  <span className="hidden sm:inline">Edit</span>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBookAppointment(patient)}
                  className="bg-orange-600/20 border-orange-500 text-orange-300 hover:bg-orange-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                  title="Book Appointment"
                >
                  <Calendar className="h-3 w-3 sm:mr-1" />
                  <span className="hidden sm:inline">Book</span>
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Patients List - Detailed View (Card Grid) */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {paginatedPatients.map((patient) => (
            <Card key={patient.id} className="glass-effect border-gray-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    {patient.profile_photo ? (
                      <img
                        src={patient.profile_photo}
                        alt={patient.full_name}
                        className="w-12 h-12 rounded-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/48';
                        }}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center">
                        <User className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-white font-semibold">{patient.full_name}</h3>
                      <p className="text-gray-400 text-sm">{patient.patient_unique_id}</p>
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded text-xs ${
                    patient.status === 'Active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                  }`}>
                    {patient.status}
                  </div>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-300">
                    <Phone className="h-4 w-4 mr-2" />
                    {patient.phone}
                  </div>
                  {patient.email && (
                    <div className="flex items-center text-sm text-gray-300">
                      <Mail className="h-4 w-4 mr-2" />
                      {patient.email}
                    </div>
                  )}
                  {patient.added_by_name && (
                    <div className="flex items-center text-sm text-gray-400">
                      <User className="h-4 w-4 mr-2" />
                      Added by: {patient.added_by_name}
                    </div>
                  )}
                  {patient.branch && (
                    <div className="flex items-center text-sm text-gray-400">
                      <User className="h-4 w-4 mr-2" />
                      Branch: {patient.branch}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-3 mt-4">
                  <div className="flex flex-row gap-2 sm:gap-3">
                    <Button
                      onClick={() => handleEditPatient(patient)}
                      className="flex-1 edit-gradient text-white hover:opacity-90 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2.5 min-w-[100px]"
                    >
                      <Edit className="h-4 w-4 flex-shrink-0" />
                      <span>Edit</span>
                    </Button>
                    <Button
                      onClick={() => handleBookAppointment(patient)}
                      className="flex-1 book-gradient text-white hover:opacity-90 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2.5 min-w-[100px]"
                    >
                      <Calendar className="h-4 w-4 flex-shrink-0" />
                      <span className="hidden sm:inline">Book Appointment</span>
                      <span className="sm:hidden">Book</span>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {filteredPatients.length === 0 && (
        <Card className="glass-effect border-gray-700">
          <CardContent className="p-8 text-center">
            <User className="h-12 w-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">
              {searchQuery ? 'No patients found matching your search' : 'No patients found'}
            </p>
            {searchQuery && (
              <Button
                variant="outline"
                onClick={() => setSearchQuery('')}
                className="mt-4 border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Clear Search
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pagination Controls */}
      {filteredPatients.length > recordsPerPage && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-700">
          <div className="text-sm text-gray-400">
            Showing {startIndex + 1} to {Math.min(endIndex, filteredPatients.length)} of {filteredPatients.length} patients
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            
            {/* Page Numbers */}
            <div className="flex items-center space-x-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // Show first page, last page, current page, and pages around current
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(page)}
                      className={
                        currentPage === page
                          ? "bg-blue-600 text-white hover:bg-blue-700"
                          : "border-gray-600 text-gray-300 hover:bg-gray-700"
                      }
                    >
                      {page}
                    </Button>
                  );
                } else if (
                  page === currentPage - 2 ||
                  page === currentPage + 2
                ) {
                  return (
                    <span key={page} className="text-gray-500 px-2">
                      ...
                    </span>
                  );
                }
                return null;
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Appointment Booking Modal */}
      <Dialog open={showAppointmentModal} onOpenChange={setShowAppointmentModal}>
        <DialogContent className="sm:max-w-[600px] glass-effect border-gray-700 bg-gray-900/95 backdrop-blur-sm">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl">New Appointment</DialogTitle>
            <DialogDescription className="text-gray-400 mt-2">
              Max 15 appointments per day
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Row 1: Patient Name and Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="appt-patient-name" className="text-white">Patient Name *</Label>
                <Input
                  id="appt-patient-name"
                  value={appointmentData.patient_name}
                  onChange={(e) => setAppointmentData({ ...appointmentData, patient_name: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Full name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="appt-phone" className="text-white">Phone *</Label>
                <Input
                  id="appt-phone"
                  value={appointmentData.phone}
                  onChange={(e) => setAppointmentData({ ...appointmentData, phone: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="e.g. 9876543210"
                  required
                />
              </div>
            </div>

            {/* Row 2: Date and Service Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="appt-date" className="text-white">Date *</Label>
                <Input
                  id="appt-date"
                  type="date"
                  value={appointmentData.date}
                  onChange={(e) => handleDateChange(e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white"
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <div>
                <Label htmlFor="appt-service" className="text-white">Service Type</Label>
                <Select 
                  value={appointmentData.service_type} 
                  onValueChange={(value) => setAppointmentData({ ...appointmentData, service_type: value })}
                >
                  <SelectTrigger id="appt-service" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Checkup">Checkup</SelectItem>
                    <SelectItem value="Cleaning">Cleaning</SelectItem>
                    <SelectItem value="Filling">Filling</SelectItem>
                    <SelectItem value="Extraction">Extraction</SelectItem>
                    <SelectItem value="Root Canal">Root Canal</SelectItem>
                    <SelectItem value="Crown">Crown</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Time Slot */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="appt-time" className="text-white">Time Slot *</Label>
                <span className="text-sm text-green-400">
                  {getAvailableSlotsCount()} slots available
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-64 overflow-y-auto p-2 bg-gray-800 rounded-lg border border-gray-600">
                {generateTimeSlots().map((slot) => {
                  const isBooked = bookedSlots.includes(slot.value);
                  const isSelected = appointmentData.time_slot === slot.value;
                  const isDisabled = isBooked || getAvailableSlotsCount() === 0;
                  
                  return (
                    <button
                      key={slot.value}
                      type="button"
                      onClick={() => !isDisabled && setAppointmentData({ ...appointmentData, time_slot: slot.value })}
                      disabled={isDisabled}
                      className={`
                        px-3 py-2 rounded text-sm font-medium transition-colors
                        ${isSelected 
                          ? 'medical-gradient text-white border-2 border-blue-400' 
                          : isBooked
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed line-through'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-600'
                        }
                        ${isDisabled ? 'opacity-50' : 'cursor-pointer'}
                      `}
                    >
                      {slot.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Row 3: Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="appt-status" className="text-white">Status</Label>
                <Select 
                  value={appointmentData.status} 
                  onValueChange={(value) => setAppointmentData({ ...appointmentData, status: value })}
                >
                  <SelectTrigger id="appt-status" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Confirmed">Confirmed</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setShowAppointmentModal(false);
                setSelectedPatientForAppointment(null);
                setAppointmentData({
                  patient_name: '',
                  phone: '',
                  date: '',
                  service_type: 'Checkup',
                  time_slot: '',
                  status: 'Pending'
                });
              }}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateAppointment}
              className="medical-gradient text-white hover:opacity-90"
            >
              Create Appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Patient Forms Wizard - Unified Modal */}
      <PatientFormsWizard
        open={showMedicalHistory}
        onClose={() => {
          setShowMedicalHistory(false);
          setSelectedPatientForHistory(null);
        }}
        patientId={selectedPatientForHistory?.id}
        patientName={selectedPatientForHistory?.name}
        onComplete={(data) => {
          if (data?.type === 'submitted') {
            // Submission completed - refresh submissions list
            if (fetchSubmissions) {
              fetchSubmissions();
            }
            // Notify parent to refresh submissions count
            if (onSubmissionComplete) {
              onSubmissionComplete();
            }
            // Dispatch event to refresh appointments
            window.dispatchEvent(new CustomEvent('appointment-submitted'));
            // Close all modals
            setShowMedicalHistory(false);
            setSelectedPatientForHistory(null);
            setShowReviewPage(false);
          } else if (data?.type === 'showReview') {
            // Close all modals
            setShowMedicalHistory(false);
            setSelectedPatientForHistory(null);
            
            // Show Review page
            setReviewPatientData(data);
            setShowReviewPage(true);
          }
        }}
      />
    </div>
  );
};

export default AdminPatientsTab;

